import React, { useState } from 'react';
import moment from 'moment';
import { commonColumns } from './columnPresets';
import { FileNameCell, StatusBadge, RemindersCell, ConnectedEventCell, CalendarRemindersCell, MeetingTitleCell, MeetingScheduledTimeCell, MeetingStatusCell, MeetingRealTimeCell, MeetingDurationCell, MeetingParticipantsCell, MeetingRecordingCell, MeetingTranscriptCell, MeetingRemindersCell, UserNameCell, TitleCell } from './cellComponents';
import infoIcon from '../../../assets/icons/info-icon.svg';

const utilityBillRoleLabels = {
  landlord: 'Landlord',
  tenant: 'Tenant',
  'community-administrator': 'Community Admin',
  'community-member': 'Community Member'
};

const utilityBillEpayStatuses = {
  paid: { label: 'Paid', color: '#28a745' },
  processing: { label: 'Processing', color: '#f0ad4e' },
  failed: { label: 'Failed', color: '#dc3545' }
};

const UtilityBillActionButton = ({ label, onClick }) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    style={{
      backgroundColor: '#002c78',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '4px 12px',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    }}
    onMouseEnter={(e) => e.target.style.backgroundColor = '#001a4d'}
    onMouseLeave={(e) => e.target.style.backgroundColor = '#002c78'}
  >
    {label}
  </button>
);

// Complete table configurations for different AppGrids components
export const tablePresets = {
  // Files table configuration
  files: {
    columns: [
      commonColumns.date('6.5%', 'createdAtDateFiles', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeFiles', 'createdAt'),
      commonColumns.user('12.5%'),
      {
        key: 'name',
        header: 'File',
        width: '12.5%',
        render: { component: FileNameCell }
      },
      {
        key: 'parentDir',
        header: 'Folder',
        width: '12.5%',
        render: (value, row) => row.parentDir?.name || 'Root'
      },
      {
        key: 'size',
        header: 'File Size',
        width: '12.5%',
        render: (value) => value || '---' // API already provides formatted size like "1.05 MB"
      },
      {
        key: 'propertyLocation',
        header: 'Property Located In',
        width: '12.5%',
        render: (value, row) => {
          return row.property ? '---' : '---'; // No property location data in API
        }
      },
      {
        key: 'propertyCity',
        header: 'City',
        width: '12.5%',
        render: (value, row) => {
          // Property is just an ID in the API, no city data available
          return row.property ? '---' : '---'; // No city data in API
        }
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '12.5%',
    emptyState: {
      text: 'No files found',
      image: null
    }
  },

  // Bank Integrations table configuration
  bankIntegrations: {
    columns: [
      commonColumns.date('6.5%', 'createdAtDateBankIntegrations', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeBankIntegrations', 'createdAt'),
      {
        key: 'user',
        header: 'User',
        width: '12.5%',
        render: (value, row) => row.user?.fullName || '---'
      },
      {
        key: 'bank',
        header: 'Bank',
        width: '12.5%',
        render: (value) => value || '---'
      },
      {
        key: 'active',
        header: 'Still active',
        width: '7%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'euaEndDate',
        header: 'Cancelation date',
        width: '9%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'euaEndTime',
        header: 'Cancelation time',
        width: '9%',
        render: (value) => value ? new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---'
      },
      {
        key: 'wallet',
        header: 'Wallet',
        width: '12.5%',
        render: (value, row) => row.wallet?.title || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '12.5%',
    emptyState: {
      text: 'No bank integrations found',
      image: null
    }
  },

  // Assistant Properties table configuration
  assistantProperties: {
    columns: [
      commonColumns.date('6%', 'createdAtDateAssistantProperties', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeAssistantProperties', 'createdAt'),
      {
        key: 'user',
        header: 'User',
        width: '12%',
        render: (value, row) => {
          return <UserNameCell value={row.user?.fullName} row={row} maxLength={18} />;
        }
      },
      {
        key: 'type',
        header: 'Type',
        width: '8%',
        render: (value) => {
          if (!value) return '---';
          return value.charAt(0).toUpperCase() + value.slice(1);
        }
      },
      {
        key: 'title',
        header: 'Request',
        width: '25%',
        render: (value) => value || '---'
      },
      {
        key: 'offersCount',
        header: 'Results',
        width: '7%',
        render: (value) => value !== null && value !== undefined ? value : '---'
      },
      {
        key: 'messagesCount',
        header: 'Queries versions',
        width: '11%',
        render: (value) => value || 0
      },
      {
        key: 'emailRequests',
        header: 'Email requests',
        width: '9%',
        render: () => '---' // Not available in API
      },
      {
        key: 'propertyLocation',
        header: 'Property Located In',
        width: '13%',
        render: (value, row) => {
          if (row.property?.countryCode === 'bg') return 'Bulgaria';
          return row.property?.countryCode?.toUpperCase() || '---';
        }
      },
      {
        key: 'propertyCity',
        header: 'City',
        width: '8%',
        render: (value, row) => row.property?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '107%',
    defaultColumnWidth: '10%',
    emptyState: {
      text: 'No assistant queries found',
      image: null
    }
  },

  // Calendar Events Properties table configuration
  calendarEventsProperties: {
    columns: [
      commonColumns.date('6.5%', 'createdAtDateCalendarEventsProperties', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeCalendarEventsProperties', 'createdAt'),
      {
        key: 'user',
        header: 'User',
        width: '12%',
        render: (value, row) => {
          return <UserNameCell value={row.user?.fullName} row={row} maxLength={18} />;
        }
      },
      {
        key: 'title',
        header: 'Title',
        width: '7.7%',
        render: (value) => value || '---'
      },
      {
        key: 'startDate',
        header: 'Event date',
        width: '7%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'description',
        header: 'Note',
        width: '5%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'location',
        header: 'Location',
        width: '6%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'attachments',
        header: 'Files',
        width: '5.5%',
        render: (value) => Array.isArray(value) && value.length > 0 ? 'Yes' : 'No'
      },
      {
        key: 'nativeCalendarEventIds',
        header: 'Saved in phone',
        width: '7.7%',
        render: (value) => Array.isArray(value) && value.length > 0 ? 'Yes' : 'No'
      },
      {
        key: 'isRecurring',
        header: 'Recurring event',
        width: '7.7%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'reminders',
        header: 'Reminders',
        width: '7.7%',
        className: 'reminders-cell',
        render: { component: CalendarRemindersCell }
      },
      {
        key: 'propertyLocation',
        header: 'Property located in',
        width: '12%',
        render: (value, row) => {
          if (row.property?.countryCode === 'bg') return 'Bulgaria';
          return row.property?.countryCode?.toUpperCase() || '---';
        }
      },
      {
        key: 'propertyCity',
        header: 'City',
        width: '7.7%',
        render: (value, row) => row.property?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '7.7%',
    emptyState: {
      text: 'No calendar events found',
      image: null
    }
  },

  // Cases table configuration
  cases: {
    columns: [
      commonColumns.date('6%', 'createdAtDateCases', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeCases', 'createdAt'),
      commonColumns.user('12.5%'),
      {
        key: 'title',
        header: 'Title',
        width: '12.5%',
        render: (value) => value || '---'
      },
      {
        key: 'type',
        header: 'Type',
        width: '12.5%',
        render: (value) => (
          <span className={`query-type ${value}`}>
            {value === 'service' ? 'Service' : 'Information'}
          </span>
        )
      },
      {
        key: 'property',
        header: 'Property',
        width: '12.5%',
        render: (value, row) => row.property?.cityEN || '---'
      },
      {
        key: 'messagesCount',
        header: 'Messages',
        width: '12.5%',
        render: (value) => value || 0
      },
      {
        key: 'offersCount',
        header: 'Offers',
        width: '12.5%',
        render: (value) => value || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '12.5%',
    emptyState: {
      text: 'No cases found',
      image: null
    }
  },

  // Chat Todos table configuration
  chatTodos: {
    columns: [
      commonColumns.date('4.5%', 'createdAtDateChatTodos', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeChatTodos', 'createdAt'),
      {
        key: 'createdBy',
        header: 'User',
        width: '7.88%',
        render: (value, row) => row.createdBy?.fullName || '---'
      },
      {
        key: 'title',
        header: 'Title',
        width: '5.88%',
        render: (value) => value || '---'
      },
      {
        key: 'participantsCount',
        header: 'Participants',
        width: '5.88%',
        render: (value) => value || '0'
      },
      {
        key: 'messagesCount',
        header: 'Messages',
        width: '5.88%',
        render: (value) => value || '0'
      },
      {
        key: 'filesCount',
        header: 'Files',
        width: '5.88%',
        render: (value) => value > 0 ? 'Yes' : 'No'
      },
      {
        key: 'voiceRecordingsCount',
        header: 'Voice messages',
        width: '5.88%',
        render: (value) => value > 0 ? 'Yes' : 'No'
      },
      {
        key: 'linksCount',
        header: 'Links',
        width: '5.88%',
        render: (value) => value > 0 ? 'Yes' : 'No'
      },
      {
        key: 'checkListsCount',
        header: 'Checklists',
        width: '5.88%',
        render: (value) => value > 0 ? 'Yes' : 'No'
      },
      {
        key: 'pinnedMessages',
        header: 'Pinned message',
        width: '5.88%',
        render: (value) => value > 0 ? 'Yes' : 'No'
      },
      {
        key: 'lastMessageSentAt',
        header: 'Last message on',
        width: '5.88%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'lastMessageSentBy',
        header: 'Last message by',
        width: '7.88%',
        render: (value, row) => row.lastMessageSentBy?.fullName || '---'
      },
      {
        key: 'archived',
        header: 'Status',
        width: '5.88%',
        render: (value) => value ? 'Archived' : 'Active'
      },
      {
        key: 'archivedAt',
        header: 'Archived on',
        width: '5.88%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'propertyLocation',
        header: 'Property located in',
        width: '5.88%',
        render: (value, row) => row.property?.cityEN || '---'
      },
      {
        key: 'propertyCity',
        header: 'City',
        width: '5.88%',
        render: (value, row) => row.property?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '170%',
    defaultColumnWidth: '5.88%',
    emptyState: {
      text: 'No chat todos found',
      image: null
    }
  },

  // Co Invitations table configuration
  coInvitations: {
    columns: [
      commonColumns.date('6%', 'createdAtDateCoInvitations', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeCoInvitations', 'createdAt'),
      {
        key: 'invitedBy',
        header: 'Invited By',
        width: '16.67%',
        render: (value, row) => row.invitedBy?.fullName || '---'
      },
      {
        key: 'property',
        header: 'Property',
        width: '16.67%',
        render: (value, row) => row.property?.cityEN || '---'
      },
      {
        key: 'acceptedBy',
        header: 'Accepted By',
        width: '16.67%',
        render: (value, row) => row.acceptedBy?.fullName || 'Pending'
      },
      {
        key: 'acceptedByStatus',
        header: 'Status',
        width: '16.67%',
        render: (value, row) => (
          <span className={`invitation-status ${row.acceptedBy ? 'accepted' : 'pending'}`}>
            {row.acceptedBy ? 'Accepted' : 'Pending'}
          </span>
        )
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '16.67%',
    emptyState: {
      text: 'No invitations found',
      image: null
    }
  },

  // Community Print Orders table configuration
  communityPrintOrders: {
    columns: [
      {
        key: 'user',
        header: 'User',
        width: '16.67%',
        render: (value, row) => row.user?.fullName || '---'
      },
      {
        key: 'communityPrintOrders',
        header: 'Community',
        width: '16.67%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'orderTotalCost',
        header: 'Total Cost',
        width: '16.67%',
        render: (value) => value || 0
      },
      {
        key: 'status',
        header: 'Status',
        width: '16.67%',
        render: (value) => (
          <span className={`order-status ${value}`}>
            {value || '---'}
          </span>
        )
      },
      {
        key: 'paymentMethod',
        header: 'Payment Method',
        width: '16.67%',
        render: (value) => value || '---'
      },
      {
        key: 'isPaid',
        header: 'Paid',
        width: '16.67%',
        render: (value) => (
          <span className={`paid-status ${value ? 'paid' : 'unpaid'}`}>
            {value ? 'Yes' : 'No'}
          </span>
        )
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '16.67%',
    emptyState: {
      text: 'No print orders found',
      image: null
    }
  },

  // Community Members table configuration
  communityMembers: {
    columns: [
      {
        key: 'joinedAt',
        header: 'Joined Date',
        width: '20%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'user',
        header: 'User',
        width: '20%',
        render: (value, row) => row.user?.fullName || '---'
      },
      {
        key: 'communityMembers',
        header: 'Community',
        width: '20%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'leftAt',
        header: 'Status',
        width: '20%',
        render: (value, row) => (
          <span className={`member-status ${row.leftAt ? 'left' : 'active'}`}>
            {row.leftAt ? 'Left' : 'Active'}
          </span>
        )
      },
      {
        key: 'isExternalUser',
        header: 'External User',
        width: '20%',
        render: (value) => (
          <span className={`external-user ${value ? 'yes' : 'no'}`}>
            {value ? 'Yes' : 'No'}
          </span>
        )
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '20%',
    emptyState: {
      text: 'No community members found',
      image: null
    }
  },

  // Community Folders table configuration
  communityFolders: {
    columns: [
      commonColumns.date('6%', 'createdAtDateCommunityFolders', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeCommunityFolders', 'createdAt'),
      {
        key: 'author',
        header: 'Author',
        width: '14.3%',
        render: (value, row) => {
          return <UserNameCell value={row.author?.fullName} row={row} maxLength={18} />;
        }
      },
      {
        key: 'name',
        header: 'Name',
        width: '14.3%',
        render: (value) => value || '---'
      },
      {
        key: 'communityFolders',
        header: 'Community',
        width: '14.3%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'filesCount',
        header: 'Files Count',
        width: '14.3%',
        render: (value) => value || 0
      },
      {
        key: 'size',
        header: 'Size',
        width: '14.3%',
        render: (value) => value || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '14.3%',
    emptyState: {
      text: 'No community folders found',
      image: null
    }
  },

  // Rent Payments table configuration
  rentPayments: {
    columns: [
      {
        key: 'createdAtDateRentPayments',
        header: 'Date',
        width: '7%',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'createdAtTimeRentPayments',
        header: 'Time',
        width: '4%',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---'
      },
      {
        key: 'user',
        header: 'User',
        width: '12%',
        render: (value, row) => {
          return <UserNameCell value={row.user?.fullName} row={row} maxLength={18} />;
        }
      },
      {
        key: 'action',
        header: 'Payment Type',
        width: '10%',
        render: (value) => {
          switch (value) {
            case 'pay-rent':
              return 'By contract';
            case 'partial-pay-rent':
              return 'Different amount';
            default:
              return value || '---';
          }
        }
      },
      {
        key: 'rentAmount',
        header: 'Default Amount',
        width: '9.09%',
        render: (value) => value ? `${value} BGN` : '---'
      },
      {
        key: 'paidAmount',
        header: 'Paid Amount',
        width: '9.09%',
        render: (value) => value ? `${value} BGN` : '---'
      },
      {
        key: 'note',
        header: 'Note',
        width: '9.09%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'attachments',
        header: 'Attached Files',
        width: '9.09%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'propertyType',
        header: 'Type',
        width: '9.09%',
        render: (value, row) => {
          // Property type not available in API yet, using placeholder
          return 'House'; // Default to House until API provides property type
        }
      },
      {
        key: 'propertyLocation',
        header: 'Property Located In',
        width: '9.09%',
        render: (value, row) => row.property?.country || '---'
      },
      {
        key: 'propertyCity',
        header: 'City',
        width: '9.09%',
        render: (value, row) => row.property?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '9.09%',
    emptyState: {
      text: 'No rent payments found',
      image: null
    }
  },

  // Folders table configuration
  folders: {
    columns: [
      commonColumns.date('6%', 'createdAtDateFolders', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeFolders', 'createdAt'),
      {
        key: 'author',
        header: 'User',
        width: '10%',
        render: (value, row) => row.author?.fullName || '---'
      },
      {
        key: 'name',
        header: 'Folder',
        width: '12%',
        render: (value) => value || '---'
      },
      {
        key: 'size',
        header: 'Folder size',
        width: '7%',
        render: (value, row) => {
          if (row.size) return row.size;
          if (row.sizeBytes) {
            const formatFileSize = (sizeBytes) => {
              if (!sizeBytes || sizeBytes === 0) return "0 B";
              const sizes = ['B', 'KB', 'MB', 'GB'];
              const i = Math.floor(Math.log(sizeBytes) / Math.log(1024));
              const size = (sizeBytes / Math.pow(1024, i)).toFixed(2);
              return `${size} ${sizes[i]}`;
            };
            return formatFileSize(row.sizeBytes);
          }
          return "0 B";
        }
      },
      {
        key: 'filesCount',
        header: 'Files',
        width: '5%',
        render: (value) => value || '0'
      },
      {
        key: 'impDate',
        header: 'Important date',
        width: '7%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'reminders',
        header: 'Reminders',
        width: '10%',
        className: 'reminders-cell',
        render: { component: CalendarRemindersCell }
      },
      {
        key: 'propertyLocation',
        header: 'Property located in',
        width: '10%',
        render: (value, row) => '- -' // Property data not populated in API
      },
      {
        key: 'propertyCity',
        header: 'City',
        width: '10%',
        render: (value, row) => '- -' // City data not populated in API
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '120%',
    defaultColumnWidth: '10%',
    emptyState: {
      text: 'No folders found',
      image: null
    }
  },

  // Services table configuration
  services: {
    columns: [
      commonColumns.date('6%', 'createdAtDateServices', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeServices', 'createdAt'),
      commonColumns.user('14.3%'),
      {
        key: 'title',
        header: 'Request',
        width: '14.3%',
        render: (value) => value || '---'
      },
      {
        key: 'type',
        header: 'Type',
        width: '14.3%',
        render: (value) => (
          <span className={`service-type ${value}`}>
            {value === 'service' ? 'Service Request' : 'Information Request'}
          </span>
        )
      },
      {
        key: 'messagesCount',
        header: 'Messages',
        width: '14.3%',
        render: (value) => value || 0
      },
      {
        key: 'offersCount',
        header: 'Offers',
        width: '14.3%',
        render: (value) => value || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '14.3%',
    emptyState: {
      text: 'No services found',
      image: null
    }
  },

  // Shared Wallets table configuration
  sharedWallets: {
    columns: [
      commonColumns.date('6%', 'createdAtDateSharedWallets', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeSharedWallets', 'createdAt'),
      {
        key: 'invitedBy',
        header: 'Invitation send by',
        width: '12.5%',
        render: (value, row) => row.invitedBy?.fullName || '---'
      },
      {
        key: 'invitedTo',
        header: 'Invitation send to',
        width: '12.5%',
        render: (value, row) => row.invitedTo?.fullName || '---'
      },
      {
        key: 'isActive',
        header: 'Still active',
        width: '7%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'canceledAtDate',
        header: 'Cancelation date',
        width: '9%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'canceledAtTime',
        header: 'Cancelation time',
        width: '9%',
        render: (value) => value ? new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---'
      },
      {
        key: 'wallet',
        header: 'Wallet',
        width: '12.5%',
        render: (value, row) => row.wallet?.title || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '12.5%',
    emptyState: {
      text: 'No shared wallets found',
      image: null
    }
  },

  // Wallets table configuration
  wallets: {
    columns: [
      commonColumns.date('5%', 'createdAtDateWallets', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeWallets', 'createdAt'),
      {
        key: 'createdBy',
        header: 'User',
        width: '10%',
        render: (value, row) => {
          return <UserNameCell value={row.createdBy?.fullName} row={row} maxLength={18} />;
        }
      },
      {
        key: 'currency',
        header: 'Currency',
        width: '5%',
        render: (value) => value?.toUpperCase() || '---'
      },
      {
        key: 'expenseCollections',
        header: 'Expense collections',
        width: '8%',
        render: (value) => value || '0'
      },
      {
        key: 'expenseCategoriesCount',
        header: 'Expense Categories',
        width: '8%',
        render: (value) => value || '0'
      },
      {
        key: 'incomeCollectionsCount',
        header: 'Income collections',
        width: '8%',
        render: (value) => value || '0'
      },
      {
        key: 'incomeCategoriesCount',
        header: 'Income categories',
        width: '8%',
        render: (value) => value || '0'
      },
      {
        key: 'expenseTransactionsCount',
        header: 'Expense transactions',
        width: '9%',
        render: (value) => value || '0'
      },
      {
        key: 'incomeTransactionsCount',
        header: 'Income transactions',
        width: '9%',
        render: (value) => value || '0'
      },
      {
        key: 'bankAccountsCount',
        header: 'Bank integrations',
        width: '7%',
        render: (value) => value || '0'
      },
      {
        key: 'authorizedUsersCount',
        header: 'Shared with users',
        width: '7%',
        render: (value) => value || '0'
      },
      {
        key: 'title',
        header: 'Wallet',
        width: '10%',
        render: (value) => value || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '130%',
    defaultColumnWidth: '10%',
    emptyState: {
      text: 'No wallets found',
      image: null
    }
  },

  // Utility Bills table configuration
  utilityBills: {
    columns: [
      {
        key: 'createdAtDateUtilityBills',
        header: 'Date',
        width: '4%',
        render: (value, row) => {
          const date = row.paymentDate || row.createdAt;
          return date ? new Date(date).toLocaleDateString('en-GB').replace(/\//g, '.') : '---';
        }
      },
      {
        key: 'createdAtTimeUtilityBills',
        header: 'Time',
        width: '4%',
        render: (value, row) => {
          const date = row.paymentDate || row.createdAt;
          return date ? new Date(date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---';
        }
      },
      {
        key: 'stripeStatus',
        header: 'Stripe Status',
        width: '4%',
        render: (value) => {
          const isStripePaid = value === "paid";
          return (
            <span style={{ color: isStripePaid ? '#28a745' : '#dc3545', fontWeight: '600' }}>
              {isStripePaid ? "Paid" : "Unpaid"}
            </span>
          );
        }
      },
      {
        key: 'epayStatus',
        header: 'Epay Status',
        width: '10%',
        filterable: true,
        filter: {
          type: 'select',
          options: [
            { value: 'processing', label: 'Processing' },
            { value: 'paid', label: 'Paid' },
            { value: 'failed', label: 'Failed' }
          ]
        },
        render: (value, row) => {
          const status = utilityBillEpayStatuses[value] || { label: 'Unpaid', color: '#dc3545' };

          if (value === "paid") {
            return (
              <span style={{ color: status.color, fontWeight: '600' }}>
                {status.label}
              </span>
            );
          }

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ color: status.color, fontWeight: '600' }}>
                {status.label}
              </span>
              <UtilityBillActionButton
                label="Mark as manually paid"
                onClick={() => {
                  if (row.onPayClick) {
                    row.onPayClick(row._id, row.epayStatus || row.status);
                  }
                }}
              />
            </div>
          );
        }
      },
      {
        key: 'clientNumber',
        header: 'Client Number',
        width: '7%',
        filterable: true,
        filter: {
          type: 'text',
          placeholder: 'Enter client number...'
        },
        render: (value) => value || '---'
      },
      {
        key: 'provider',
        header: 'Provider',
        width: '14%',
        filterable: true,
        filter: {
          type: 'text',
          placeholder: 'Enter provider...'
        },
        render: (value, row) => row.utbProvider?.label || '---'
      },
      {
        key: 'amount',
        header: 'Total Amount',
        width: '6%',
        filterable: true,
        filter: {
          type: 'numberRange'
        },
        render: (value, row) => {
          const price = row.price ?? row.amount;
          if (price === null || price === undefined || price === '' || isNaN(Number(price))) {
            return "---";
          }
          const currency = row.currency || 'eur';
          return `${Number(price).toFixed(2)} ${currency === 'eur' ? '€' : currency.toUpperCase()}`;
        }
      },
      {
        key: 'hasReceipt',
        header: 'Receipt',
        width: '11%',
        filterable: true,
        filter: {
          type: 'select',
          options: [
            { value: 'yes', label: 'With receipt' },
            { value: 'no', label: 'Without receipt' }
          ]
        },
        render: (value, row) => {
          if (!row.receiptUrl) {
            return '---';
          }
          if (/stripe\.com/i.test(row.receiptUrl)) {
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ color: '#dc3545', fontWeight: '600' }}>
                  Missing Epay invoice
                </span>
                <UtilityBillActionButton
                  label="Upload"
                  onClick={() => {
                    if (row.onUploadInvoiceClick) {
                      row.onUploadInvoiceClick(row._id);
                    }
                  }}
                />
              </div>
            );
          }
          return (
            <a
              href={row.receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ color: '#002c78', fontWeight: '600', textDecoration: 'underline' }}
            >
              View
            </a>
          );
        }
      },
      {
        key: 'user',
        header: 'User',
        width: '12%',
        filterable: true,
        filter: {
          type: 'text',
          placeholder: 'Enter user name...'
        },
        render: (value, row) => row.paidBy?.fullName || '---'
      },
      {
        key: 'role',
        header: 'Role',
        width: '5%',
        render: (value) => utilityBillRoleLabels[value] || '---'
      },
      {
        key: 'address',
        header: 'Property Address',
        width: '10%',
        render: (value, row) => row.property?.address || '---'
      },
      {
        key: 'city',
        header: 'City',
        width: '7%',
        render: (value, row) => row.property?.city || '---'
      },
      {
        key: 'propertyType',
        header: 'Property Type',
        width: '6%',
        render: (value, row) => row.property?.propertyType || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: true,
      sorting: false,
      rowSelection: true,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '140%',
    defaultColumnWidth: '8.33%',
    emptyState: {
      text: 'No utility bills found',
      image: null
    }
  },

  // Free Users table configuration
  freeUsers: {
    columns: [
      {
        key: 'fullName',
        header: 'User',
        width: '20%',
        filterable: true,
        filter: {
          type: 'text',
          placeholder: 'Enter user name...'
        },
        render: (value, row) => {
          return <UserNameCell value={value} row={row} maxLength={25} />;
        }
      },
      {
        key: 'email',
        header: 'Email',
        width: '25%',
        filterable: true,
        filter: {
          type: 'text',
          placeholder: 'Enter email address...'
        },
        render: (value) => (
          <span style={{ textTransform: 'none' }}>
            {value || '---'}
          </span>
        )
      },
      {
        key: 'createdAt',
        header: 'User Since',
        width: '20%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'propertiesCount',
        header: 'Properties',
        width: '12%',
        render: (value) => value || 0
      },
      {
        key: 'walletsCount',
        header: 'Wallets',
        width: '12%',
        render: (value) => value || 0
      },
      {
        key: 'bankAccountsCount',
        header: 'Bank Accounts',
        width: '11%',
        render: (value) => value || 0
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: true,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '16.66%',
    emptyState: {
      text: 'No free users found',
      image: null
    }
  },

  // Subscribed Users table configuration
  subscribedUsers: {
    columns: [
      {
        key: 'subscriptionValidUntil',
        header: 'Expires on',
        width: '15%',
        render: (value) => {
          if (!value) return '---';
          return new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.');
        }
      },
      {
        key: 'fullName',
        header: 'User',
        width: '15%',
        filterable: true,
        filter: {
          type: 'text',
          placeholder: 'Enter user name...'
        },
        render: (value, row) => {
          return <UserNameCell value={value} row={row} maxLength={20} />;
        }
      },
      {
        key: 'email',
        header: 'Email',
        width: '20%',
        filterable: true,
        filter: {
          type: 'text',
          placeholder: 'Enter email address...'
        },
        render: (value) => (
          <span style={{ textTransform: 'none' }}>
            {value || '---'}
          </span>
        )
      },
      {
        key: 'createdAt',
        header: 'User Since',
        width: '15%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'propertiesCount',
        header: 'Properties',
        width: '12%',
        render: (value) => value || 0
      },
      {
        key: 'walletsCount',
        header: 'Wallets',
        width: '12%',
        render: (value) => value || 0
      },
      {
        key: 'bankAccountsCount',
        header: 'Bank Accounts',
        width: '11%',
        render: (value) => value || 0
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: true,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '16.66%',
    emptyState: {
      text: 'No subscribed users found',
      image: null
    }
  },

  // Free Communities table configuration
  freeCommunities: {
    columns: [
      {
        key: 'nickname',
        header: 'Community',
        width: '30%',
        render: (value) => value || '---'
      },
      {
        key: 'createdAt',
        header: 'Added on',
        width: '25%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'createdBy',
        header: 'Added by',
        width: '25%',
        render: (value) => {
          if (value && value.fullName) {
            return value.fullName;
          }
          return '---';
        }
      },
      {
        key: 'participantsCount',
        header: 'Participants',
        width: '20%',
        render: (value) => value || 0
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '25%',
    emptyState: {
      text: 'No free communities found',
      image: null
    }
  },

  // Subscribed Communities table configuration
  subscribedCommunities: {
    columns: [
      {
        key: 'nickname',
        header: 'Community',
        width: '30%',
        render: (value) => value || '---'
      },
      {
        key: 'createdAt',
        header: 'Added on',
        width: '25%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'createdBy',
        header: 'Added by',
        width: '25%',
        render: (value) => {
          if (value && value.fullName) {
            return value.fullName;
          }
          return '---';
        }
      },
      {
        key: 'participantsCount',
        header: 'Participants',
        width: '20%',
        render: (value) => value || 0
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '25%',
    emptyState: {
      text: 'No subscribed communities found',
      image: null
    }
  },

  // Community Utility Bills table configuration
  communityUtilityBills: {
    columns: [
      {
        key: 'createdAtDateCommunityUtilityBills',
        header: 'Date',
        width: '7%',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'createdAtTimeCommunityUtilityBills',
        header: 'Time',
        width: '4%',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---'
      },
      {
        key: 'stripeStatus',
        header: 'Stripe Status',
        width: '8%',
        render: (value, row) => {
          // Use the new dedicated stripeStatus field from API
          const isStripePaid = value === "paid";
          return (
            <span style={{ color: isStripePaid ? '#28a745' : '#dc3545', fontWeight: '600' }}>
              {isStripePaid ? "Paid" : "Unpaid"}
            </span>
          );
        }
      },
      {
        key: 'epayStatus',
        header: 'Epay Status',
        width: '8%',
        render: (value, row) => {
          // Use the new dedicated epayStatus field from API
          const isEpayPaid = value === "paid";
          
          if (isEpayPaid) {
            return (
              <span style={{ color: '#28a745', fontWeight: '600' }}>
                Paid
              </span>
            );
          } else {
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ color: '#dc3545', fontWeight: '600' }}>
                  Unpaid
                </span>
                <button
                  onClick={() => {
                    console.log("🏢 Communities Pay button clicked! Row:", row._id, "EpayStatus:", row.epayStatus, "Status:", row.status);     
                    console.log("🏢 onPayClick function exists:", !!row.onPayClick);
                    // Handle pay button click - will open modal - pass epayStatus instead of status
                    if (row.onPayClick) {
                      row.onPayClick(row._id, row.epayStatus || row.status);
                    } else {
                      console.warn("🏢 onPayClick function not found on row object");
                    }
                  }}
                  style={{
                    backgroundColor: '#002c78',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#001a4d'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#002c78'}
                >
                  Pay
                </button>
              </div>
            );
          }
        }
      },
      {
        key: 'paidBy',
        header: 'User',
        width: '14%',
        render: (value, row) => row.paidBy?.fullName || '---'
      },
      {
        key: 'paymentMethod',
        header: 'Payment Method',
        width: '12%',
        render: (value) => {
          if (!value) return '---';
          switch(value) {
            case 'card': return 'Card';
            case 'apple-pay': return 'Apple Pay';
            case 'google-pay': return 'Google Pay';
            default: return value;
          }
        }
      },
      {
        key: 'utbType',
        header: 'Utility Type',
        width: '14%',
        render: (value, row) => row.utbType?.label || '---'
      },
      {
        key: 'utbProvider',
        header: 'Provider',
        width: '16%',
        render: (value, row) => row.utbProvider?.label || '---'
      },
      {
        key: 'price',
        header: 'Amount',
        width: '10%',
        render: (value) => {
          if (!value && value !== 0) return "---";
          return `${value.toFixed(2)} BGN`;
        }
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '108%',
    defaultColumnWidth: '12%',
    emptyState: {
      text: 'No community utility bills found',
      image: null
    }
  },

  // Transactions table configuration
  transactions: {
    columns: [
      commonColumns.date('6.5%', 'createdAtDateTransactions', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeTransactions', 'createdAt'),
      {
        key: 'user',
        header: 'User',
        width: '14.3%',
        render: (value, row) => row.user?.fullName || '---'
      },
      {
        key: 'entryType',
        header: 'Entry',
        width: '10%',
        render: (value) => value === 'manual' ? 'Manual' : 'Bank account'
      },
      {
        key: 'type',
        header: 'Type',
        width: '7%',
        render: (value) => (
          <span className={`transaction-type ${value}`}>
            {value === 'income' ? 'Income' : 'Expense'}
          </span>
        )
      },
      {
        key: 'amount',
        header: 'Amount',
        width: '14.3%',
        render: (value, row) => (
          <span className={`amount ${row.type}`}>
            {row.type === 'income' ? '+' : '-'}{value} {row.currency?.toUpperCase()}
          </span>
        )
      },
      {
        key: 'wallet',
        header: 'Wallet',
        width: '14.3%',
        render: (value, row) => row.wallet?.title || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '14.3%',
    emptyState: {
      text: 'No transactions found',
      image: null
    }
  },

  // Chats Communities table configuration
  chatsCommunities: {
    columns: [
      {
        key: 'createdAtDateChatsCommunities',
        header: 'Date',
        width: '3.5%',
        marginLeft: '0px',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'createdAtTimeChatsCommunities',
        header: 'Time',
        width: '2.5%',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---'
      },
      {
        key: 'createdBy',
        header: 'User',
        width: '7%',
        render: (value, row) => {
          return <UserNameCell value={row.createdBy?.fullName} row={row} maxLength={18} />;
        }
      },
      {
        key: 'title',
        header: 'Title',
        width: '6%',
        render: (value) => {
          return <TitleCell value={value} maxLength={22} />;
        }
      },
      {
        key: 'participantsCount',
        header: 'Participants',
        width: '3.5%',
        render: (value) => value || 0
      },
      {
        key: 'messagesCount',
        header: 'Messages',
        width: '3.5%',
        render: (value) => value || 0
      },
      {
        key: 'hasFiles',
        header: 'Files',
        width: '3%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'hasVoiceMessages',
        header: 'Voice messages',
        width: '4%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'hasLinks',
        header: 'Links',
        width: '3%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'hasChecklists',
        header: 'Checklists',
        width: '4%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'hasPinnedMessage',
        header: 'Pinned message',
        width: '4.5%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'lastMessageSentAt',
        header: 'Last message on',
        width: '4.5%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'lastMessageSentBy',
        header: 'Last message by',
        width: '6%',
        render: (value, row) => row.lastMessageSentBy?.fullName || '---'
      },
      {
        key: 'archived',
        header: 'Status',
        width: '3%',
        render: (value, row) => {
          // Check if archived or has archivedAt date
          if (value === true || row.archivedAt) {
            return 'Archived';
          }
          return 'Active';
        }
      },
      {
        key: 'archivedAt',
        header: 'Archived on',
        width: '8%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB') : '- -'
      },
      {
        key: 'communityLocation',
        header: 'Community located in',
        width: '7%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'communityCity',
        header: 'City',
        width: '8%',
        render: (value, row) => row.community?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '200%',
    defaultColumnWidth: '6.25%',
    emptyState: {
      text: 'No community chats found',
      image: null
    }
  },

  // Calendar Communities table configuration
  calendarCommunities: {
    columns: [
      commonColumns.date('4%', 'createdAtDateCalendarCommunities', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeCalendarCommunities', 'createdAt'),
      {
        key: 'user',
        header: 'User',
        width: '13.85%',
        render: (value, row) => row.user?.fullName || '---'
      },
      {
        key: 'title',
        header: 'Title',
        width: '9%',
        render: (value) => {
          return <TitleCell value={value} maxLength={25} />;
        }
      },
      {
        key: 'startDate',
        header: 'Event Date',
        width: '4%',
        render: (value) => {
          if (!value) return '---';
          return new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.');
        }
      },
      {
        key: 'description',
        header: 'Note',
        width: '3%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'location',
        header: 'Location',
        width: '3%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'attachments',
        header: 'Files',
        width: '3%',
        render: (value) => Array.isArray(value) && value.length > 0 ? 'Yes' : 'No'
      },
      {
        key: 'nativeCalendarEventIds',
        header: 'Connected Event',
        width: '5%',
        className: 'connected-event-cell',
        render: { component: ConnectedEventCell }
      },
      {
        key: 'isRecurring',
        header: 'Recurring Event',
        width: '5%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'reminders',
        header: 'Reminders',
        width: '5%',
        className: 'reminders-cell',
        render: { component: CalendarRemindersCell }
      },
      {
        key: 'communityLocation',
        header: 'Community Located In',
        width: '7%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'communityCity',
        header: 'City',
        width: '13.85%',
        render: (value, row) => row.community?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '180%',
    defaultColumnWidth: '13.85%',
    emptyState: {
      text: 'No community calendar events found',
      image: null
    }
  },

  // Administrators Communities table configuration
  administratorsCommunities: {
    columns: [
      {
        key: 'adminSinceDate',
        header: 'Date',
        width: '7%',
        render: (value, row) => row.adminSince ? new Date(row.adminSince).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'adminSinceTime',
        header: 'Time',
        width: '6%',
        render: (value, row) => row.adminSince ? new Date(row.adminSince).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---'
      },
      {
        key: 'fullName',
        header: 'User',
        width: '12%',
        render: (value) => value || '---'
      },
      {
        key: 'type',
        header: 'Admin type',
        width: '12%',
        render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '---'
      },
      {
        key: 'communityLocation',
        header: 'Community located in',
        width: '10%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'communityCity',
        header: 'City',
        width: '10%',
        render: (value, row) => row.community?.cityEN || '---'
      }
    ],
    rowKey: (row, index) => `${row._id || 'admin'}_${row.community?._id || 'community'}_${row.type || 'type'}_${index}`,
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '110%',
    defaultColumnWidth: '16.7%',
    emptyState: {
      text: 'No community administrators found',
      image: null
    }
  },

  // FilesCommunities table configuration
  filesCommunities: {
    columns: [
      commonColumns.date('6%', 'createdAtDateFilesCommunities', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeFilesCommunities', 'createdAt'),
      {
        key: 'author',
        header: 'User',
        width: '10%',
        render: (value, row) => {
            return <UserNameCell value={row.createdBy?.fullName} row={row} maxLength={18} />;
        }
      },
      {
        key: 'name',
        header: 'File',
        width: '16%',
        render: { component: FileNameCell, props: { maxLength: 30 } }
      },
      {
        key: 'parentDir',
        header: 'Folder',
        width: '11%',
        render: (value, row) => row.parentDir?.name || 'Root'
      },
      {
        key: 'size',
        header: 'File size',
        width: '6%',
        render: (value) => value || '---'
      },
      {
        key: 'communityLocation',
        header: 'Community located in',
        width: '13%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'communityCity',
        header: 'City',
        width: '8%',
        render: (value, row) => row.community?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '10%',
    emptyState: {
      text: 'No community files found',
      image: null
    }
  },

  // FoldersCommunities table configuration
  foldersCommunities: {
    columns: [
      commonColumns.date('6%', 'createdAtDateFoldersCommunities', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeFoldersCommunities', 'createdAt'),
      {
        key: 'author',
        header: 'Author',
        width: '14%',
        render: (value, row) => row.author?.fullName || '---'
      },
      {
        key: 'name',
        header: 'Name',
        width: '14%',
        render: (value) => value || '---'
      },
      {
        key: 'impDate',
        header: 'Important Date',
        width: '14%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'impMessage',
        header: 'Message',
        width: '14%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'filesCount',
        header: 'Files Count',
        width: '14%',
        render: (value) => value || 0
      },
      {
        key: 'size',
        header: 'Size',
        width: '14%',
        render: (value) => value || '0 B'
      },
      {
        key: 'communityFoldersCommunities',
        header: 'Community',
        width: '14%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'communityCity',
        header: 'City',
        width: '14%',
        render: (value, row) => row.community?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '140%',
    defaultColumnWidth: '14%',
    emptyState: {
      text: 'No community folders found',
      image: null
    }
  },

  // Meetings Communities table configuration
  meetingsCommunities: {
    columns: [
      {
        key: 'createdAtDateMeetingsCommunities',
        header: 'Date',
        width: '4%',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'createdAtTimeMeetingsCommunities',
        header: 'Time',
        width: '3%',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---'
      },
      {
        key: 'user',
        header: 'User',
        width: '8%',
        render: (value, row) => {
            return <UserNameCell value={row.createdBy?.fullName} row={row} maxLength={18} />;
        }
      },
      {
        key: 'title',
        header: 'Title',
        width: '7%',
        render: { component: 'MeetingTitleCell' }
      },
      {
        key: 'startDate',
        header: 'Date of event',
        width: '4%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'startTime',
        header: 'Scheduled time',
        width: '5%',
        render: { component: 'MeetingScheduledTimeCell' }
      },
      {
        key: 'status',
        header: 'Status',
        width: '4%',
        render: { component: 'MeetingStatusCell' }
      },
      {
        key: 'startedAt',
        header: 'Real time',
        width: '4%',
        render: { component: 'MeetingRealTimeCell' }
      },
      {
        key: 'duration',
        header: 'Duration',
        width: '4%',
        render: { component: 'MeetingDurationCell' }
      },
      {
        key: 'participantsCount',
        header: 'Participants',
        width: '4%',
        render: (value) => value || 0
      },
      {
        key: 'recordingStatus',
        header: 'Recording',
        width: '4%',
        render: { component: 'MeetingRecordingCell' }
      },
      {
        key: 'transcriptionStatus',
        header: 'Transcript',
        width: '4%',
        render: { component: 'MeetingTranscriptCell' }
      },
      {
        key: 'transcriptLang',
        header: 'Transcript language',
        width: '6%',
        render: (value) => value || '---'
      },
      {
        key: 'topicsCount',
        header: 'Topics',
        width: '3%',
        render: (value) => value > 0 ? 'Yes' : 'No'
      },
      {
        key: 'filesCount',
        header: 'Files',
        width: '3%',
        render: (value) => value > 0 ? 'Yes' : 'No'
      },
      {
        key: 'calendarEvent',
        header: 'Reminders',
        width: '4%',
        render: { component: 'MeetingRemindersCell' }
      },
      {
        key: 'communityMeetings',
        header: 'Community located in',
        width: '6%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'communityCityMeetings',
        header: 'City',
        width: '6%',
        render: (value, row) => row.community?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '200%',
    defaultColumnWidth: '6.25%',
    emptyState: {
      text: 'No community meetings found',
      image: null
    }
  },

  // Notices table configuration
  notices: {
    columns: [
      commonColumns.date('5%', 'createdAtDateNotices', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimeNotices', 'createdAt'),
      {
        key: 'author',
        header: 'User',
        width: '11%',
        render: (value, row) => {
          return <UserNameCell value={row.author?.fullName} row={row} maxLength={18} />;
        }
      },
      {
        key: 'title',
        header: 'Title',
        width: '12%',
        render: (value) => {
          return <TitleCell value={value} maxLength={25} />;
        }
      },
      {
        key: 'seenByCount',
        header: 'Seen by members',
        width: '8%',
        render: (value) => value || 0
      },
      {
        key: 'filesCount',
        header: 'Photo',
        width: '5%',
        render: (value) => value > 0 ? 'Yes' : 'No'
      },
      {
        key: 'refType',
        header: 'Connected message',
        width: '8%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'communityNotices',
        header: 'Community located in',
        width: '10%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'communityCityNotices',
        header: 'City',
        width: '14.4%',
        render: (value, row) => row.community?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '130%',
    defaultColumnWidth: '14.4%',
    emptyState: {
      text: 'No notices found',
      image: null
    }
  },

  // Participants Communities table configuration
  participantsCommunities: {
    columns: [
      {
        key: 'joinedAtDateParticipants',
        header: 'Date',
        width: '5%',
        render: (value, row) => row.joinedAt ? new Date(row.joinedAt).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'joinedAtTimeParticipants', 
        header: 'Time',
        width: '4%',
        render: (value, row) => row.joinedAt ? new Date(row.joinedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---'
      },
      {
        key: 'user',
        header: 'User',
        width: '12%',
        render: (value, row) => {
            return <UserNameCell value={row.createdBy?.fullName} row={row} maxLength={18} />;
        }
      },
      {
        key: 'leftAtActiveParticipants',
        header: 'Still active',
        width: '5%',
        render: (value, row) => row.leftAt ? 'No' : 'Yes'
      },
      {
        key: 'leftAtDateParticipants',
        header: 'Date of cancelation',
        width: '8.5%',
        render: (value, row) => row.leftAt ? new Date(row.leftAt).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'leftAtTimeParticipants',
        header: 'Time of cancelation',
        width: '8.5%',
        render: (value, row) => row.leftAt ? new Date(row.leftAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---'
      },
      {
        key: 'communityParticipants',
        header: 'Community located in',
        width: '10%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'communityCityParticipants',
        header: 'City',
        width: '8%',
        render: (value, row) => row.community?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '120%',
    defaultColumnWidth: '12.5%',
    emptyState: {
      text: 'No community participants found',
      image: null
    }
  },

  // Polls table configuration
  polls: {
    columns: [
      commonColumns.date('6%', 'createdAtDatePolls', 'createdAt'),
      commonColumns.time('4%', 'createdAtTimePolls', 'createdAt'),
      {
        key: 'createdBy',
        header: 'Creator',
        width: '10%',
        render: (value, row) => row.createdBy?.fullName || '---'
      },
      {
        key: 'communityPolls',
        header: 'Community',
        width: '10%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'question',
        header: 'Question',
        width: '10%',
        render: (value) => value || '---'
      },
      {
        key: 'options',
        header: 'Options',
        width: '10%',
        render: (value) => value?.length || 0
      },
      {
        key: 'totalVotes',
        header: 'Votes',
        width: '10%',
        render: (value) => value || 0
      },
      {
        key: 'status',
        header: 'Status',
        width: '10%',
        render: (value) => value === 'active' ? 'Active' : 'Ended'
      },
      {
        key: 'type',
        header: 'Type',
        width: '10%',
        render: (value) => value === 'meeting' ? 'Meeting' : 'General'
      },
      {
        key: 'participantsCount',
        header: 'Participants',
        width: '10%',
        render: (value) => value || 0
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '140%',
    defaultColumnWidth: '10%',
    emptyState: {
      text: 'No polls found',
      image: null
    }
  },

  // Payments table configuration
  payments: {
    columns: [
      {
        key: '_idDatePayments',
        header: 'Date',
        width: '6%',
        render: (value, row) => {
          // Extract date from ObjectId timestamp
          if (row._id && typeof row._id === 'string' && row._id.length === 24) {
            try {
              const timestamp = parseInt(row._id.substring(0, 8), 16) * 1000;
              return new Date(timestamp).toLocaleDateString('en-GB').replace(/\//g, '.');
            } catch (e) {
              return '---';
            }
          }
          return '---';
        }
      },
      {
        key: '_idTimePayments',
        header: 'Time',
        width: '4%',
        render: (value, row) => {
          // Extract time from ObjectId timestamp
          if (row._id && typeof row._id === 'string' && row._id.length === 24) {
            try {
              const timestamp = parseInt(row._id.substring(0, 8), 16) * 1000;
              return new Date(timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
            } catch (e) {
              return '---';
            }
          }
          return '---';
        }
      },
      {
        key: 'createdBy',
        header: 'User',
        width: '12%',
        render: (value, row) => {
            return <UserNameCell value={row.createdBy?.fullName} row={row} maxLength={18} />;
        }
      },
      {
        key: 'goal',
        header: 'Amount',
        width: '8%',
        render: (value, row) => {
          const currency = row.currency?.toUpperCase() || 'USD';
          return value ? `${value} ${currency}` : '---';
        }
      },
      {
        key: 'raised',
        header: 'Progress',
        width: '10%',
        render: (value, row) => {
          const raised = value || 0;
          const goal = row.goal || 0;
          const currency = row.currency?.toUpperCase() || 'USD';
          return goal > 0 ? `${raised}/${goal} ${currency}` : '---';
        }
      },
      {
        key: 'closedAt',
        header: 'Status',
        width: '6%',
        render: (value) => value ? 'Completed' : 'Open'
      },
      {
        key: 'withdrawable',
        header: 'Withdrawal status',
        width: '12%',
        render: (value, row) => {
          return (row.withdrawn > 0) ? 'Withdrawn' : 'Not withdrawn';
        }
      },
      {
        key: 'withdrawn',
        header: 'Withdrawn',
        width: '14%',
        render: (value, row) => {
          if (value > 0) {
            const currency = row.currency?.toUpperCase() || 'USD';
            return `${value} ${currency}`;
          }
          return '- -';
        }
      },
      {
        key: 'communityPayments',
        header: 'Community located in',
        width: '10%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'communityCityPayments',
        header: 'City',
        width: '8%',
        render: (value, row) => row.community?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '102%',
    defaultColumnWidth: '10%',
    emptyState: {
      text: 'No community funds found',
      image: null
    },
    customFooter: (data) => {
      // Calculate total collected but not withdrawn money in BGN
      const calculateTotal = () => {
        let totalBGN = 0;
        
        data.forEach(row => {
          const raised = row.raised || 0;
          const withdrawn = row.withdrawn || 0;
          const currency = row.currency?.toLowerCase();
          
          if (currency === 'bgn') {
            totalBGN += (raised - withdrawn);
          }
        });
        
        return totalBGN.toFixed(2);
      };
      
      const total = calculateTotal();
      
      return (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          backgroundColor: '#002C78',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 'bold',
          textTransform: 'uppercase',
          width: '560px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: 1000
        }}>
          COLLECTED BUT NOT WITHDRAWN MONEY: {total} BGN
        </div>
      );
    }
  },

  // Assistant Communities table configuration
  assistantCommunities: {
    columns: [
      {
        key: 'createdAtDateAssistant',
        header: 'Date',
        width: '5%',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'createdAtTimeAssistant',
        header: 'Time',
        width: '3.5%',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---'
      },
      {
        key: 'user',
        header: 'User',
        width: '10%',
        render: (value, row) => {
            return <UserNameCell value={row.createdBy?.fullName} row={row} maxLength={18} />;
        }
      },
      {
        key: 'type',
        header: 'Type',
        width: '6%',
        render: (value) => {
          if (!value) return '---';
          return value.charAt(0).toUpperCase() + value.slice(1);
        }
      },
      {
        key: 'title',
        header: 'Request',
        width: '20%',
        render: (value) => value || '---'
      },
      {
        key: 'offersCount',
        header: 'Result',
        width: '5%',
        render: (value) => value > 0 ? value : '- -'
      },
      {
        key: 'messagesCount',
        header: 'Queries versions',
        width: '8%',
        render: (value) => value || 0
      },
      {
        key: 'emailRequests',
        header: 'Email requests',
        width: '8%',
        render: () => '---'
      },
      {
        key: 'communityAssistant',
        header: 'Community located in',
        width: '10%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'communityCityAssistant',
        header: 'City',
        width: '4%',
        render: (value, row) => row.community?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '140%',
    defaultColumnWidth: '10%',
    emptyState: {
      text: 'No community assistant queries found',
      image: null
    }
  },

  // Print Materials Communities table configuration
  printMaterialsCommunities: {
    columns: [
      {
        key: 'createdAtDatePrintMaterials',
        header: 'Date',
        width: '5.5%',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'createdAtTimePrintMaterials',
        header: 'Time',
        width: '4%',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---'
      },
      {
        key: 'user',
        header: 'User',
        width: '10%',
        render: (value, row) => {
            return <UserNameCell value={row.createdBy?.fullName} row={row} maxLength={18} />;
        }
      },
      {
        key: 'lang',
        header: 'Language',
        width: '5%',
        render: (value) => value ? value.toUpperCase() : '---'
      },
      {
        key: 'communityPrintMaterials',
        header: 'Community located in',
        width: '10%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'communityCityPrintMaterials',
        header: 'City',
        width: '10%',
        render: (value, row) => row.community?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '120%',
    defaultColumnWidth: '16.7%',
    emptyState: {
      text: 'No print materials found',
      image: null
    }
  },

  // Transactions Communities table configuration
  transactionsCommunities: {
    columns: [
      {
        key: 'createdAtDate',
        header: 'Date',
        width: '6.5%',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'createdAtTime',
        header: 'Time',
        width: '5%',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---'
      },
      {
        key: 'entryType',
        header: 'Entry',
        width: '10%',
        render: (value) => value === 'manual' ? 'Manual' : 'Bank account'
      },
      {
        key: 'type',
        header: 'Type',
        width: '7%',
        render: (value) => value === 'income' ? 'Income' : 'Expense'
      },
      {
        key: 'amount',
        header: 'Amount',
        width: '10%',
        render: (value, row) => {
          const currency = row.currency?.toUpperCase() || 'USD';
          return value ? `${value} ${currency}` : '---';
        }
      },
      {
        key: 'repeat',
        header: 'Repeat',
        width: '5%',
        render: (value) => value || 'No'
      },
      {
        key: 'methodOfDistribution',
        header: 'Method of distribution',
        width: '12%',
        render: (value) => value || '---'
      },
      {
        key: 'applicableFor',
        header: 'Applicable for',
        width: '12%',
        render: (value) => value || '---'
      },
      {
        key: 'communityLocation',
        header: 'Community located in',
        width: '10%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'communityCity',
        header: 'City',
        width: '8%',
        render: (value, row) => row.community?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '10%',
    emptyState: {
      text: 'No community transactions found',
      image: null
    }
  },

  // Wallets Communities table configuration
  walletsCommunities: {
    columns: [
      {
        key: 'createdAtDate',
        header: 'Date',
        width: '4%',
        render: (value, row) => row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'createdAtTime',
        header: 'Time',
        width: '3%',
        render: (value, row) => row.createdAt ? moment(row.createdAt).format('HH:mm') : '---'
      },
      {
        key: 'createdBy',
        header: 'User',
        width: '4%',
        render: (value, row) => row.createdBy?.fullName || 'System'
      },
      {
        key: 'currency',
        header: 'Currency',
        width: '3%',
        render: (value) => value ? value.toUpperCase() : '---'
      },
      {
        key: 'expenseCollections',
        header: 'Expense collections',
        width: '6%',
        render: (value) => value || 0
      },
      {
        key: 'expenseCategoriesCount',
        header: 'Expense categories',
        width: '6%',
        render: (value) => value || 0
      },
      {
        key: 'incomeCollectionsCount',
        header: 'Income collections',
        width: '6%',
        render: (value) => value || 0
      },
      {
        key: 'incomeCategoriesCount',
        header: 'Income categories',
        width: '6%',
        render: (value) => value || 0
      },
      {
        key: 'expenseTransactionsCount',
        header: 'Expense transactions',
        width: '7%',
        render: (value) => value || 0
      },
      {
        key: 'incomeTransactionsCount',
        header: 'Income transactions',
        width: '7%',
        render: (value) => value || 0
      },
      {
        key: 'bankAccountsCount',
        header: 'Bank integrations',
        width: '6%',
        render: (value) => value || 0
      },
      {
        key: 'communityLocation',
        header: 'Community located in',
        width: '10%',
        render: (value, row) => row.community?.nickname || '---'
      },
      {
        key: 'communityCity',
        header: 'City',
        width: '6%',
        render: (value, row) => row.community?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '180%',
    defaultColumnWidth: '7.7%',
    emptyState: {
      text: 'No community wallets found',
      image: null
    }
  },

  // Landlord Tenant Invitations table configuration
  landlordTenantInvitations: {
    columns: [
      {
        key: 'createdAtDate',
        header: 'Date',
        width: '5.5%',
        render: (value, row) => row.createdAt ? moment(row.createdAt).format('DD.MM.YYYY') : '---'
      },
      {
        key: 'createdAtTime',
        header: 'Time',
        width: '4%',
        render: (value, row) => row.createdAt ? moment(row.createdAt).format('HH:mm') : '---'
      },
      {
        key: 'invitedBy',
        header: 'Invitation sent by',
        width: '10%',
        render: (value, row) => row.invitedBy?.fullName || '---'
      },
      {
        key: 'senderRole',
        header: 'Role',
        width: '6%',
        render: (value, row) => row.senderRole || '---'
      },
      {
        key: 'acceptedBy',
        header: 'Invitation sent to',
        width: '8%',
        render: (value, row) => row.acceptedBy?.fullName || '---'
      },
      {
        key: 'receiverRole',
        header: 'Role',
        width: '6%',
        render: (value, row) => row.receiverRole || '---'
      },
      {
        key: 'status',
        header: 'Status',
        width: '6%',
        render: (value, row) => {
          let status = 'pending';
          if (row.acceptedAt) {
            status = 'accepted';
          } else if (row.leftAt) {
            status = 'cancelled';
          }
          return (
            <span className={`invitation-status ${status}`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          );
        }
      },
      {
        key: 'active',
        header: 'Still active',
        width: '5%',
        render: (value, row) => {
          const isActive = row.acceptedAt && !row.leftAt;
          return isActive ? 'Yes' : 'No';
        }
      },
      {
        key: 'leftAtDate',
        header: 'Date of cancelation',
        width: '8%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'leftAtTime',
        header: 'Time of cancelation',
        width: '8%',
        render: (value) => value ? new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---'
      },
      {
        key: 'propertyLocation',
        header: 'Property located in',
        width: '8%',
        render: (value, row) => row.property?.address || '---'
      },
      {
        key: 'propertyCity',
        header: 'City',
        width: '10%',
        render: (value, row) => row.property?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '130%',
    defaultColumnWidth: '10%',
    emptyState: {
      text: 'No landlord-tenant invitations found',
      image: null
    }
  },

  // Shared Management Invitations table configuration
  sharedManagementInvitations: {
    columns: [
      {
        key: 'createdAtDate',
        header: 'Date',
        width: '5.5%',
        render: (value, row) => row.createdAt ? moment(row.createdAt).format('DD.MM.YYYY') : '---'
      },
      {
        key: 'createdAtTime',
        header: 'Time',
        width: '4%',
        render: (value, row) => row.createdAt ? moment(row.createdAt).format('HH:mm') : '---'
      },
      {
        key: 'invitedBy',
        header: 'Invitation sent by',
        width: '10%',
        render: (value, row) => row.invitedBy?.fullName || '---'
      },
      {
        key: 'role',
        header: 'Role',
        width: '6%',
        render: (value, row) => row.role || '---'
      },
      {
        key: 'acceptedBy',
        header: 'Invitation sent to',
        width: '10%',
        render: (value, row) => row.acceptedBy?.fullName || '---'
      },
      {
        key: 'status',
        header: 'Status',
        width: '6%',
        render: (value, row) => {
          let status = 'pending';
          if (row.acceptedAt) {
            status = 'accepted';
          } else if (row.leftAt) {
            status = 'cancelled';
          }
          return (
            <span className={`invitation-status ${status}`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          );
        }
      },
      {
        key: 'active',
        header: 'Still active',
        width: '6%',
        render: (value, row) => {
          const isActive = row.acceptedAt && !row.leftAt;
          return isActive ? 'Yes' : 'No';
        }
      },
      {
        key: 'leftAtDate',
        header: 'Cancelation date',
        width: '8%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'leftAtTime',
        header: 'Cancelation time',
        width: '8%',
        render: (value) => value ? new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '---'
      },
      {
        key: 'propertyLocation',
        header: 'Property located in',
        width: '10%',
        render: (value, row) => row.property?.address || '---'
      },
      {
        key: 'propertyCity',
        header: 'City',
        width: '10%',
        render: (value, row) => row.property?.cityEN || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '120%',
    defaultColumnWidth: '10%',
    emptyState: {
      text: 'No shared management invitations found',
      image: null
    }
  },

  // Helper function for country name mapping
  getCountryName: (countryCode) => {
    const countries = {
      'bg': 'Bulgaria',
      'gr': 'Greece', 
      'ro': 'Romania',
      'ru': 'Russia',
      'us': 'United States',
      'nz': 'New Zealand',
      'de': 'Germany',
      'fr': 'France',
      'it': 'Italy',
      'es': 'Spain',
      'uk': 'United Kingdom',
      'gb': 'United Kingdom',
      'ca': 'Canada',
      'au': 'Australia',
      'jp': 'Japan',
      'kr': 'South Korea',
      'cn': 'China',
      'in': 'India',
      'br': 'Brazil',
      'mx': 'Mexico',
      'ar': 'Argentina',
      'tr': 'Turkey',
      'pl': 'Poland',
      'nl': 'Netherlands',
      'se': 'Sweden',
      'no': 'Norway',
      'dk': 'Denmark',
      'fi': 'Finland',
      'ch': 'Switzerland',
      'at': 'Austria',
      'be': 'Belgium',
      'pt': 'Portugal',
      'ie': 'Ireland',
      'cz': 'Czech Republic',
      'hu': 'Hungary',
      'sk': 'Slovakia',
      'si': 'Slovenia',
      'hr': 'Croatia',
      'rs': 'Serbia',
      'ua': 'Ukraine',
      'ee': 'Estonia',
      'lv': 'Latvia',
      'lt': 'Lithuania'
    };
    return countries[countryCode?.toLowerCase()] || countryCode?.toUpperCase() || '---';
  },

  // Communities List table configuration
  communitiesList: {
    columns: [
      {
        key: 'nickname',
        header: 'Community Name',
        width: '14%',
        render: (value) => <span className="community-name">{value || '---'}</span>
      },
      {
        key: 'countryCode',
        header: 'Country',
        width: '12%',
        render: (value) => (
          <span className="community-country">
            {tablePresets.getCountryName(value)}
          </span>
        )
      },
      {
        key: 'cityEN',
        header: 'City',
        width: '10%',
        render: (value) => (
          <span className="community-city">
            {value || '---'}
          </span>
        )
      },
      {
        key: 'membersCount',
        header: 'Participants',
        width: '8%',
        render: (value) => <span className="participant-count">{value || 0}</span>
      },
      {
        key: 'propertiesCount',
        header: 'Added Properties',
        width: '9%',
        render: (value) => <span className="properties-count">{value || 0}</span>
      },
      {
        key: 'activePropertiesCount',
        header: 'Active Properties',
        width: '9%',
        render: (value) => <span className="active-properties-count">{value || 0}</span>
      },
      {
        key: 'adminsCount',
        header: 'Admins',
        width: '7%',
        render: (value) => <span className="admin-count">{value || 0}</span>
      },
      {
        key: 'createdAt',
        header: 'Added On',
        width: '10%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'createdBy',
        header: 'Added By',
        width: '11%',
        render: (value, row) => (
          row.createdBy ? row.createdBy.fullName || row.createdBy.email || '---' : 
          <span className="placeholder-text">Not available</span>
        )
      },
      {
        key: 'usedStorage',
        header: 'Storage Used',
        width: '6%',
        render: (value) => <span className="storage-used">{value || '0'}</span>
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: true,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '16.66%',
    emptyState: {
      text: 'No communities found',
      image: null
    }
  },

  // Users List Table Configuration
  usersList: {
    columns: [
      {
        key: 'fullName',
        header: 'Name',
        width: '10%',
        render: (value) => value || '---'
      },
      {
        key: 'country',
        header: 'Country',
        width: '8%',
        render: (value) => value || '---'
      },
      {
        key: 'city',
        header: 'City',
        width: '8%',
        render: (value) => value || '---'
      },
      {
        key: 'os',
        header: 'OS',
        width: '6%',
        render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '---'
      },
      {
        key: 'lang',
        header: 'Language',
        width: '7%',
        render: (value) => value ? value.toUpperCase() : '---'
      },
      {
        key: 'subscriptionType',
        header: 'Subscriptions',
        width: '8%',
        render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '---'
      },
      {
        key: 'enabledNotifications',
        header: 'Notifications',
        width: '8%',
        render: (value) => value ? 'Yes' : 'No'
      },
      {
        key: 'hasLocation',
        header: 'Location',
        width: '6%',
        render: (value, row) => (row.country && row.city) ? 'Yes' : 'No'
      },
      {
        key: 'phoneNumber',
        header: 'Phone number',
        width: '10%',
        render: (value) => value || '---'
      },
      {
        key: 'email',
        header: 'Email',
        width: '13%',
        render: (value) => value ? (
          <span style={{ textTransform: 'none' }}>{value}</span>
        ) : '---'
      },
      {
        key: 'createdAt',
        header: 'User since',
        width: '8%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'properties',
        header: 'Added properties',
        width: '10%',
        render: (value, row) => {
          const count = value ? value.length : 0;
          
          // Simple Tooltip Component for ≤5 properties
          const SimplePropertyTooltip = ({ properties }) => {
            const [showTooltip, setShowTooltip] = useState(false);
            
            return (
              <div 
                className="info-icon-wrapper"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                style={{ 
                  position: 'relative', 
                  display: 'inline-block',
                  padding: '5px'
                }}
              >
                <img 
                  src={infoIcon} 
                  alt="Info" 
                  style={{ 
                    width: '16px', 
                    height: '16px', 
                    cursor: 'pointer', 
                    opacity: 0.7,
                    filter: 'brightness(0) saturate(100%) invert(50%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.5) contrast(1)',
                    transition: 'opacity 0.2s ease, filter 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.opacity = '1';
                    e.target.style.filter = 'brightness(0) saturate(100%) invert(30%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.3) contrast(1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.opacity = '0.7';
                    e.target.style.filter = 'brightness(0) saturate(100%) invert(50%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.5) contrast(1)';
                  }}
                />
                {showTooltip && (
                  <div className="info-tooltip">
                    <div className="tooltip-content property-tooltip" style={{
                      padding: '12px 15px',
                      minWidth: '180px',
                      maxWidth: '280px'
                    }}>
                      {properties.map((property, index) => (
                        <div key={index} className="property-item" style={{
                          marginBottom: index < properties.length - 1 ? '12px' : '0'
                        }}>
                          <div 
                            className="property-address" 
                            style={{ 
                              fontSize: '12px', 
                              color: '#000', 
                              fontWeight: 'normal', 
                              marginBottom: '4px',
                              lineHeight: '1.3',
                              wordBreak: 'break-word'
                            }}
                            title={property.address}
                          >
                            {property.address}
                          </div>
                          <div 
                            className="property-role" 
                            style={{ 
                              fontSize: '15px', 
                              lineHeight: '1.2',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <span style={{ color: '#000', fontWeight: 'bold' }}>Role:</span>
                            <span style={{ color: '#1DD7A3', fontWeight: 'bold', textTransform: 'capitalize' }}>
                              {property.role === 'owner' ? 'Owner' :
                               property.role === 'landlord' ? 'Landlord' :
                               property.role === 'tenant' ? 'Tenant' :
                               property.role === 'landlordWithTenant' ? 'Landlord with tenant' :
                               property.role === 'landlordWithoutTenant' ? 'Landlord without tenant' :
                               property.role === 'tenantWithoutLandlord' ? 'Tenant without landlord' :
                               property.role === 'communityManager' ? 'Community Manager' :
                               property.role === 'userWithoutProperty' ? 'User without property' :
                               property.role}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="tooltip-arrow"></div>
                  </div>
                )}
              </div>
            );
          };

          // Modal Component for >5 properties
          const PropertyModal = ({ properties }) => {
            const [isOpen, setIsOpen] = useState(false);
            
            const handleClickOutside = (e) => {
              if (e.target.classList.contains('modal-overlay')) {
                setIsOpen(false);
              }
            };

            return (
              <>
                <div 
                  className="info-icon-wrapper"
                  style={{ 
                    position: 'relative', 
                    display: 'inline-block',
                    padding: '5px'
                  }}
                >
                  <img 
                    src={infoIcon} 
                    alt="View Properties" 
                    style={{ 
                      width: '16px', 
                      height: '16px', 
                      cursor: 'pointer', 
                      opacity: 0.7,
                      filter: 'brightness(0) saturate(100%) invert(40%) sepia(100%) saturate(1000%) hue-rotate(200deg) brightness(0.8) contrast(1)',
                      transition: 'opacity 0.2s ease, filter 0.2s ease'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsOpen(!isOpen);
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = '1';
                      e.target.style.filter = 'brightness(0) saturate(100%) invert(40%) sepia(100%) saturate(1200%) hue-rotate(200deg) brightness(0.9) contrast(1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = '0.7';
                      e.target.style.filter = 'brightness(0) saturate(100%) invert(40%) sepia(100%) saturate(1000%) hue-rotate(200deg) brightness(0.8) contrast(1)';
                    }}
                  />
                </div>

                {isOpen && (
                  <div 
                    className="modal-overlay"
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      zIndex: 9999,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={handleClickOutside}
                  >
                    <div 
                      className="modal-content"
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '20px',
                        maxWidth: '500px',
                        maxHeight: '80vh',
                        width: '90%',
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                        position: 'relative'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px',
                        paddingBottom: '10px',
                        borderBottom: '1px solid #e9ecef'
                      }}>
                        <h3 style={{
                          margin: 0,
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: '#333'
                        }}>
                          User Properties ({properties.length})
                        </h3>
                        <button
                          onClick={() => setIsOpen(false)}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#666',
                            padding: '0',
                            width: '30px',
                            height: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          ×
                        </button>
                      </div>
                      
                      <div style={{
                        maxHeight: '60vh',
                        overflowY: 'auto',
                        padding: '0 10px'
                      }}>
                        {properties.map((property, index) => (
                          <div key={index} style={{
                            padding: '15px',
                            marginBottom: '10px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '6px',
                            border: '1px solid #e9ecef'
                          }}>
                            <div style={{ 
                              fontSize: '14px', 
                              color: '#333', 
                              fontWeight: 'normal', 
                              marginBottom: '8px',
                              lineHeight: '1.4',
                              wordBreak: 'break-word'
                            }}>
                              <strong>Address:</strong> {property.address}
                            </div>
                            <div style={{ 
                              fontSize: '14px', 
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span style={{ color: '#000' }}>Role:</span>
                              <span style={{ color: '#1DD7A3', textTransform: 'capitalize' }}>
                                {property.role === 'owner' ? 'Owner' :
                                property.role === 'landlord' ? 'Landlord' :
                                property.role === 'tenant' ? 'Tenant' :
                                property.role === 'landlordWithTenant' ? 'Landlord with tenant' :
                                property.role === 'landlordWithoutTenant' ? 'Landlord without tenant' :
                                property.role === 'tenantWithoutLandlord' ? 'Tenant without landlord' :
                                property.role === 'communityManager' ? 'Community Manager' :
                                property.role === 'userWithoutProperty' ? 'User without property' :
                                property.role}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          };

          // Main component logic
          const PropertyInfoCell = () => {
            return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: '15px' }}>
                <span>{count}</span>
                {count > 0 && (
                  count <= 5 ? (
                    <SimplePropertyTooltip properties={value} />
                  ) : (
                    <PropertyModal properties={value} />
                  )
                )}
              </div>
            );
          };
          
          return <PropertyInfoCell />;
        }
      },
      {
        key: 'managingCommunitiesCount',
        header: 'Managing communities',
        width: '12%',
        render: (value) => value || '0'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '100%',
    defaultColumnWidth: '7.69%',
    emptyState: {
      text: 'No users found',
      image: null
    }
  },

  // Properties List Table Configuration  
  propertiesList: {
    columns: [
      {
        key: 'countryCode',
        header: 'Country',
        width: '5%',
        render: (value) => tablePresets.getCountryName(value)
      },
      {
        key: 'cityEN',
        header: 'City',
        width: '5%',
        render: (value) => value || '---'
      },
      {
        key: 'propertyType',
        header: 'Type',
        width: '6%',
        render: (value) => value?.name || '---'
      },
      {
        key: 'status',
        header: 'Status',
        width: '5%',
        render: (value, row) => {
          // Defensive programming - ensure row exists
          if (!row) return 'Unknown';
          
          const rent = row.rent;
          const role = row.createdBy?.role;

          // Property with rent = someone is paying rent
          if (rent && rent > 0) {
            // Owner/landlord receiving rent = "Rented out"
            if (role === 'landlord' || role === 'owner') {
              return 'Rented out';
            } else {
              // Other roles paying rent = "Rented" 
              return 'Rented';
            }
          }

          // Property without rent = owned but not rented
          if (!rent || rent === 0 || rent === null) {
            return 'Owned';
          }

          return 'Owned'; // fallback
        }
      },
      {
        key: 'rent',
        header: 'Monthly rent',
        width: '5%',
        render: (value, row) => {
          // Defensive programming - ensure row exists
          if (!row) return '---';
          
          if (value && value > 0) {
            return `${value} ${row.currency?.toUpperCase() || 'BGN'}`;
          }
          return '---';
        }
      },
      {
        key: 'usersCount',
        header: 'Shared with users',
        width: '7%',
        render: (value) => value || '0'
      },
      {
        key: 'totalStorage',
        header: 'Total storage',
        width: '5%',
        render: () => 'TODO' // Not available in API
      },
      {
        key: 'createdAt',
        header: 'Added on',
        width: '5%',
        render: (value) => value ? new Date(value).toLocaleDateString('en-GB').replace(/\//g, '.') : '---'
      },
      {
        key: 'createdBy',
        header: 'Added by',
        width: '8.5%',
        render: (value) => {
          // Defensive programming - ensure value exists
          if (!value) return '---';
          return value.fullName || '---';
        }
      },
      {
        key: 'phoneNumber',
        header: 'Phone number',
        width: '6%',
        render: () => 'TODO' // Not available in current API
      },
      {
        key: 'createdBy',
        header: 'Role',
        width: '5%',
        render: (value) => {
          // Defensive programming - ensure value exists
          if (!value) return 'Owner';
          
          const role = value.role;
          if (!role) return 'Owner';
          return role.charAt(0).toUpperCase() + role.slice(1);
        }
      },
      {
        key: 'countryCode',
        header: 'Property located in',
        width: '6%',
        render: (value) => tablePresets.getCountryName(value)
      },
      {
        key: 'cityEN',
        header: 'City',
        width: '6%',
        render: (value) => value || '---'
      }
    ],
    features: {
      infiniteScroll: true,
      filtering: false,
      sorting: false,
      rowSelection: false,
      bulkActions: false,
      export: false,
      search: false
    },
    tableWidth: '140%',
    defaultColumnWidth: '10.8%',
    emptyState: {
      text: 'No properties found',
      image: null
    }
  }
}; 